// components/formDialog/StudentEditDialog.tsx
import { Spinner } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import InputField from "@/components/form/components/InputField";
import { useUpdateStudentDetailMutation } from "@/hooks/api/student.queries";
import {
  fetchStudentDetailById,
  type BatchStudentDetailType,
  type StudentUpdateType,
} from "@/services/student.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const editSchema = z.object({
  name: z.string().optional(),
  email: z
    .string()
    .optional()
    .refine((v) => !v || z.string().email().safeParse(v).success, {
      message: "Enter a valid email",
    }),
  phone: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{10}$/.test(v), {
      message: "Phone must be exactly 10 digits",
    }),
  cgpa: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().min(0).max(10).optional()
  ),
  current_semester: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().int().min(1).optional()
  ),
  current_year: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().int().min(1).optional()
  ),
});

type EditFormType = {
  name?: string;
  email?: string;
  phone?: string;
  cgpa?: number;
  current_semester?: number;
  current_year?: number;
};

type StudentEditDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  /** Pre-loaded detail (e.g. from the detail page). If omitted, it's fetched. */
  student?: BatchStudentDetailType;
  /**
   * Immediate values for auto-fill (e.g. the list row), used as fallback when
   * the detail response omits them (full_name/phone may be absent in detail).
   */
  seed?: { name?: string; email?: string; phone?: string; cgpa?: number };
};

const StudentEditDialog = ({
  isOpen,
  onClose,
  studentId,
  student: preloaded,
  seed,
}: StudentEditDialogProps) => {
  const updateMutation = useUpdateStudentDetailMutation(studentId);

  // When no detail is passed in (list page), fetch it so the form can auto-fill.
  const { data: fetched, isLoading } = useQuery({
    queryKey: ["students", "detail", studentId],
    queryFn: () => fetchStudentDetailById(studentId),
    enabled: isOpen && !!studentId && !preloaded,
  });

  const student = preloaded ?? fetched;

  const form = useForm<EditFormType>({
    resolver: zodResolver(editSchema) as any,
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      cgpa: undefined,
      current_semester: undefined,
      current_year: undefined,
    },
  });

  // Reset form with current student data once it's available. Prefer detail
  // values, fall back to the seed (list row) so the form always auto-fills.
  useEffect(() => {
    if (!isOpen) return;
    if (!student && !seed) return;
    form.reset({
      name: student?.full_name ?? student?.name ?? seed?.name ?? "",
      email:
        (student as { org_email?: string })?.org_email ?? seed?.email ?? "",
      phone: student?.phone ?? seed?.phone ?? "",
      cgpa: student?.cgpa ?? seed?.cgpa ?? undefined,
      current_semester: student?.current_semester ?? undefined,
      current_year: student?.current_year ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, student, seed?.name, seed?.email, seed?.phone, seed?.cgpa]);

  const handleSubmit = (values: EditFormType) => {
    const payload: StudentUpdateType = {
      ...(values.name?.trim() ? { name: values.name.trim() } : {}),
      ...(values.email?.trim() ? { org_email: values.email.trim() } : {}),
      ...(values.phone?.trim() ? { phone: values.phone.trim() } : {}),
      ...(values.cgpa != null ? { cgpa: values.cgpa } : {}),
      ...(values.current_semester != null
        ? { current_semester: values.current_semester }
        : {}),
      ...(values.current_year != null
        ? { current_year: values.current_year }
        : {}),
    };

    updateMutation.mutate(payload, { onSuccess: onClose });
  };

  const rollNo =
    student?.current_enrollment?.roll_number ?? student?.roll_number ?? "—";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Edit Student</DialogTitle>
        </DialogHeader>

        {isLoading && !student && !seed ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              Roll number:{" "}
              <span className="font-medium text-gray-800">{rollNo}</span>
            </p>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              >
                <InputField<EditFormType>
                  name="name"
                  label="Name"
                  placeholder="Full name"
                  type="text"
                  disabled={updateMutation.isPending}
                />
                <InputField<EditFormType>
                  name="email"
                  label="Email"
                  placeholder="name@example.com"
                  type="email"
                  disabled={updateMutation.isPending}
                />
                <InputField<EditFormType>
                  name="phone"
                  label="Phone"
                  placeholder="10-digit mobile number"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={updateMutation.isPending}
                />
                <InputField<EditFormType>
                  name="cgpa"
                  label="CGPA"
                  placeholder="e.g. 8.5"
                  type="number"
                  disabled={updateMutation.isPending}
                />
                <InputField<EditFormType>
                  name="current_semester"
                  label="Current Semester"
                  placeholder="e.g. 3"
                  type="number"
                  disabled={updateMutation.isPending}
                />
                <InputField<EditFormType>
                  name="current_year"
                  label="Current Year"
                  placeholder="e.g. 2"
                  type="number"
                  disabled={updateMutation.isPending}
                />
                <DialogFooter className="col-span-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StudentEditDialog;
